import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import cls from "./ClubMemberRequestsSingle.module.scss";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const statusMeta = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "accepted") return { label: "Accepted", tone: "good" };
  if (s === "rejected") return { label: "Rejected", tone: "bad" };
  return { label: "Pending", tone: "warn" };
};

const fmt = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString();
};

const pickUrl = (signed) => {
  if (!signed) return "";
  if (typeof signed === "string") return signed;
  return signed.url || "";
};

const safeText = (v) => (v == null ? "" : String(v));

const ClubMemberRequestsSingle = () => {
  const nav = useNavigate();
  const { clubId, clubJoinRequestId } = useParams();

  const base = useMemo(
    () =>
      `/v1/admin/cities/clubs/${clubId}/membership-requests/${clubJoinRequestId}`,
    [clubId, clubJoinRequestId],
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [club, setClub] = useState(null);
  const [request, setRequest] = useState(null);

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const status = request?.status || "pending";
  const meta = statusMeta(status);

  const backPath = useMemo(
    () => `/admin/clubs/single/${clubId}/membership-requests`,
    [clubId],
  );

  const canAccept = status === "pending" || status === "rejected";
  const canReject = status === "pending" || status === "accepted";

  const fetchOne = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(base);
      setClub(data?.club || null);
      setRequest(data?.request || null);

      // reset reject form if status changed
      setShowRejectForm(false);
      setRejectReason("");
    } catch (e) {
      setErr(
        e?.response?.data?.message || e?.message || "Failed to load request",
      );
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  const onAccept = async () => {
    if (!clubId || !clubJoinRequestId) return;
    setSaving(true);
    setErr("");
    try {
      await api.post(`${base}/admin-accept`);
      await fetchOne();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to accept");
    } finally {
      setSaving(false);
    }
  };

  const onStartReject = () => {
    setShowRejectForm(true);
  };

  const onCancelReject = () => {
    setShowRejectForm(false);
    setRejectReason("");
  };

  const onSubmitReject = async (e) => {
    e?.preventDefault?.();
    if (!clubId || !clubJoinRequestId) return;

    const reason = rejectReason.trim();
    if (!reason) {
      setErr("Please add a reason before rejecting.");
      return;
    }

    setSaving(true);
    setErr("");
    try {
      await api.post(`${base}/admin-reject`, { reason });
      await fetchOne();
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2?.message || "Failed to reject");
    } finally {
      setSaving(false);
    }
  };

  const sender = request?.sender || null;
  const decidedBy = request?.decidedBy || null;

  const senderAvatar = pickUrl(sender?.avatar);
  const deciderAvatar = pickUrl(decidedBy?.avatar);

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <Motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className={cls.header}
        >
          <div className={cls.headerTop}>
            <div className={cls.titleBlock}>
              <div className={cls.kicker}>Club Membership Request</div>
              <h1 className={cls.title}>Request Details</h1>

              <div className={cls.breadcrumbs}>
                <Link to={backPath} className={cls.backLink}>
                  ← Back to Requests
                </Link>
              </div>
            </div>

            <div className={cls.headerRight}>
              <span
                className={`${cls.statusPill} ${cls[`status_${meta.tone}`]}`}
              >
                {meta.label}
              </span>

              <div className={cls.headerMini}>
                <span className={cls.miniLabel}>Last update</span>
                <span className={cls.miniValue}>
                  {fmt(request?.lastUpdatedAt || request?.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          {err ? (
            <div className={cls.alertErr} role="alert">
              {err}
            </div>
          ) : null}
        </Motion.div>

        <AnimatePresence mode="wait">
          {loading ? (
            <Motion.div
              key="loading"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0 }}
              className={cls.loadingCard}
            >
              <div className={cls.loaderDotRow}>
                <span className={cls.dot} />
                <span className={cls.dot} />
                <span className={cls.dot} />
              </div>
              <div className={cls.loadingText}>Loading request…</div>
            </Motion.div>
          ) : (
            <Motion.div
              key="content"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0 }}
              className={cls.grid}
            >
              {/* Club Info */}
              <section className={cls.card}>
                <div className={cls.cardTop}>
                  <h2 className={cls.cardTitle}>Club Info</h2>
                  <div className={cls.pillRow}>
                    <span className={cls.pill}>
                      Club No: <b>{club?.clubNo ?? "-"}</b>
                    </span>
                    <span className={cls.pill}>
                      Club: <b>{safeText(club?.clubName || "-")}</b>
                    </span>
                  </div>
                </div>

                <div className={cls.kvGrid}>
                  <div className={cls.kv}>
                    <div className={cls.k}>City</div>
                    <div className={cls.v}>
                      {club?.city?.cityName || "-"}
                      {club?.city?.cityId ? (
                        <span className={cls.subId}>
                          ID: {club.city.cityId}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className={cls.kv}>
                    <div className={cls.k}>Street</div>
                    <div className={cls.v}>
                      {club?.street?.streetName || "-"}
                      {club?.street?.streetId ? (
                        <span className={cls.subId}>
                          ID: {club.street.streetId}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className={cls.kv}>
                    <div className={cls.k}>Club ID</div>
                    <div className={cls.vMono}>
                      {safeText(club?.clubId || clubId)}
                    </div>
                  </div>

                  <div className={cls.kv}>
                    <div className={cls.k}>Request ID</div>
                    <div className={cls.vMono}>
                      {safeText(
                        request?.clubJoinRequestId || clubJoinRequestId,
                      )}
                    </div>
                  </div>
                </div>

                <div className={cls.actionsRow}>
                  <Link
                    to={`/admin/clubs/single/${clubId}`}
                    className={`${cls.btn} ${cls.btnGhost}`}
                  >
                    View Club
                  </Link>
                  <button
                    type="button"
                    className={`${cls.btn} ${cls.btnGhost}`}
                    onClick={() => nav(backPath)}
                  >
                    View All Requests
                  </button>
                </div>
              </section>

              {/* Request People */}
              <section className={cls.card}>
                <div className={cls.cardTop}>
                  <h2 className={cls.cardTitle}>People</h2>
                  <div className={cls.pillRow}>
                    <span className={cls.pill}>
                      Status: <b>{meta.label}</b>
                    </span>
                    <span className={cls.pill}>
                      Created: <b>{fmt(request?.createdAt)}</b>
                    </span>
                  </div>
                </div>

                <div className={cls.peopleGrid}>
                  {/* Sender */}
                  <div className={cls.personCard}>
                    <div className={cls.personTop}>
                      <div className={cls.avatarWrap}>
                        {senderAvatar ? (
                          <img
                            className={cls.avatar}
                            src={senderAvatar}
                            alt="Sender avatar"
                          />
                        ) : (
                          <div className={cls.avatarFallback}>?</div>
                        )}
                      </div>

                      <div className={cls.personInfo}>
                        <div className={cls.personRole}>Sender</div>
                        <div className={cls.personName}>
                          {safeText(sender?.username || "-")}
                        </div>
                        <div className={cls.personMeta}>
                          <span className={cls.miniTag}>userId</span>
                          <span className={cls.monoSmall}>
                            {safeText(sender?.userId || "-")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decided By */}
                  <div className={cls.personCard}>
                    <div className={cls.personTop}>
                      <div className={cls.avatarWrap}>
                        {deciderAvatar ? (
                          <img
                            className={cls.avatar}
                            src={deciderAvatar}
                            alt="Decider avatar"
                          />
                        ) : (
                          <div className={cls.avatarFallback}>
                            {decidedBy ? "?" : "—"}
                          </div>
                        )}
                      </div>

                      <div className={cls.personInfo}>
                        <div className={cls.personRole}>Decision By</div>
                        <div className={cls.personName}>
                          {decidedBy
                            ? safeText(decidedBy?.username || "-")
                            : "Not decided yet"}
                        </div>

                        <div className={cls.personMeta}>
                          <span className={cls.miniTag}>userId</span>
                          <span className={cls.monoSmall}>
                            {decidedBy
                              ? safeText(decidedBy?.userId || "-")
                              : "-"}
                          </span>
                        </div>

                        <div className={cls.personMeta}>
                          <span className={cls.miniTag}>decidedAt</span>
                          <span className={cls.monoSmall}>
                            {fmt(request?.decidedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className={cls.messageBlock}>
                  <div className={cls.messageLabel}>Message / Notes</div>
                  <div className={cls.messageText}>
                    {request?.message ? (
                      safeText(request.message)
                    ) : (
                      <span className={cls.dim}>No message</span>
                    )}
                  </div>
                </div>
              </section>

              {/* Actions */}
              <section className={cls.card}>
                <div className={cls.cardTop}>
                  <h2 className={cls.cardTitle}>Actions</h2>
                  <div className={cls.helpLine}>
                    Switch the decision anytime. Accept activates access. Reject
                    removes access (admin action).
                  </div>
                </div>

                <div className={cls.actionArea}>
                  {/* Buttons Row (dynamic) */}
                  <AnimatePresence initial={false}>
                    {!showRejectForm ? (
                      <Motion.div
                        key="buttons"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        className={cls.btnRow}
                      >
                        {canAccept ? (
                          <button
                            type="button"
                            className={`${cls.btn} ${cls.btnGreen}`}
                            onClick={onAccept}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Accept"}
                          </button>
                        ) : null}

                        {canReject ? (
                          <button
                            type="button"
                            className={`${cls.btn} ${cls.btnRed}`}
                            onClick={onStartReject}
                            disabled={saving}
                          >
                            Reject
                          </button>
                        ) : null}
                      </Motion.div>
                    ) : (
                      <Motion.form
                        key="rejectForm"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        className={cls.rejectForm}
                        onSubmit={onSubmitReject}
                      >
                        <div className={cls.rejectTitle}>Reject reason</div>
                        <div className={cls.rejectHint}>
                          This will switch status to <b>Rejected</b>. Add a
                          short reason (shown in logs/notifications).
                        </div>

                        <textarea
                          className={cls.textarea}
                          rows={5}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Type the reason…"
                          disabled={saving}
                        />

                        <div className={cls.formBtns}>
                          <button
                            type="button"
                            className={`${cls.btn} ${cls.btnGhost}`}
                            onClick={onCancelReject}
                            disabled={saving}
                          >
                            Cancel
                          </button>

                          <button
                            type="submit"
                            className={`${cls.btn} ${cls.btnRed}`}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Reject"}
                          </button>
                        </div>
                      </Motion.form>
                    )}
                  </AnimatePresence>

                  <div className={cls.metaStrip}>
                    <div className={cls.metaItem}>
                      <span className={cls.metaK}>Created</span>
                      <span className={cls.metaV}>
                        {fmt(request?.createdAt)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaK}>Updated</span>
                      <span className={cls.metaV}>
                        {fmt(request?.updatedAt)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaK}>Decided</span>
                      <span className={cls.metaV}>
                        {fmt(request?.decidedAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </Motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ClubMemberRequestsSingle;
